import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "./PorDetail.css";
import { API_URL } from "../config";
import { useI18n } from "../i18n/I18nProvider";

function truncateMetaText(text, max = 160) {
  if (!text || typeof text !== "string") return "";
  const cleaned = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, Math.max(0, max - 1))}…`;
}

function PorDetailBackLink() {
  const { t } = useI18n();
  return (
    <div className="por-detail-toolbar">
      <Link to="/portfolio" className="por-detail-back">
        <span className="por-detail-back__icon" aria-hidden>
          ←
        </span>
        {t("detail.backPortfolio")}
      </Link>
    </div>
  );
}

function PorDetailNotFound() {
  const { t } = useI18n();
  return (
    <div className="por-detail-page">
      <Helmet>
        <title>{t("meta.pages.portfolioDetailNotFound.title")}</title>
        <meta
          name="description"
          content={t("meta.pages.portfolioDetailNotFound.description")}
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <section className="por-detail-404 layout_padding">
        <div className="container">
          <div className="por-detail-404__card">
            <p className="por-detail-404__code" aria-hidden>
              404
            </p>
            <h1 className="por-detail-404__title">{t("detail.notFoundTitle")}</h1>
            <p className="por-detail-404__hint">{t("detail.notFoundHint")}</p>
            <Link
              to="/portfolio"
              className="por-detail-404__cta pro-btn pro-btn--primary"
            >
              {t("detail.backPortfolio")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PorDetail() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { t } = useI18n();
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setProduct(null);

    const fetchProductData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/works/${id}`);
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (response.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (!response.ok) {
          setError(tRef.current("detail.error"));
          setLoading(false);
          return;
        }

        setProduct(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching product data:", err);
        if (!cancelled) {
          setError(tRef.current("detail.error"));
          setLoading(false);
        }
      }
    };

    fetchProductData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="por-detail-page">
        <section className="por-detail-state layout_padding">
          <div className="container">
            <PorDetailBackLink />
            <p className="por-detail-state__text">{t("detail.loading")}</p>
          </div>
        </section>
      </div>
    );
  }

  if (notFound) {
    return <PorDetailNotFound />;
  }

  if (error) {
    return (
      <div className="por-detail-page">
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <section className="por-detail-state layout_padding">
          <div className="container">
            <PorDetailBackLink />
            <p className="por-detail-state__text por-detail-state__text--error">{error}</p>
          </div>
        </section>
      </div>
    );
  }

  if (!product) {
    return <PorDetailNotFound />;
  }

  const images = product.images || [];
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const canonical = `${origin}${pathname}`;
  const brand = t("meta.brand");
  const pageTitle = t("meta.portfolioDetail.projectTitle", {
    name: product.name_site,
    brand,
  });
  const pageDesc = truncateMetaText(
    t("meta.portfolioDetail.projectDescription", {
      name: product.name_site,
      tech: product.technologie || "",
      brand,
    }),
    160
  );
  const ogImage =
    images.length > 0
      ? `${API_URL}${images[0]}`
      : `${origin}/images/logo.png`;

  const workJsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: product.name_site,
    url: canonical,
    description: pageDesc,
    ...(images.length > 0 ? { image: [`${API_URL}${images[0]}`] } : {}),
  };

  return (
    <div className="por-detail-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">
          {JSON.stringify(workJsonLd)}
        </script>
      </Helmet>
      <div className="contact_section layout_padding por-detail-page__section">
        <div className="container">
          <PorDetailBackLink />
          <h1 className="titlesites">{product.name_site}</h1>
          <div className="product-info">
            <div className="product-carousel">
              {images.length > 0 ? (
                <Carousel>
                  {images.map((image, index) => (
                    <div key={index}>
                      <img
                        src={`${API_URL}${image}`}
                        alt={t("detail.imageAlt", { n: index + 1 })}
                      />
                    </div>
                  ))}
                </Carousel>
              ) : (
                <p className="blog_text">{t("detail.notFound")}</p>
              )}
            </div>
            <div className="product-description">
              <div className="product-price">
                <h2>
                  <strong>{t("detail.tech")} </strong>
                  {product.technologie}
                </h2>
                {product.url_site && (
                  <div className="btnliensite">
                    <div className="callnow_bt">
                      <a
                        href={product.url_site}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-discover="true"
                        className="pro-btn pro-btn--primary"
                      >
                        {t("detail.viewSite")}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PorDetail;
